import React, { useState } from 'react';
import useStyles from './styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import TextField from '@material-ui/core/TextField';
import { otherRatesInfo } from '../Parameters';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { useMutation, useApolloClient } from '@apollo/react-hooks';
import { FloatingRates } from '../../../graphql/rates/types/FloatingRates';
import { EditFloatingRateVariables } from '../../../graphql/rates/types/EditFloatingRate';
import { EDIT_FLOATING_RATE } from '../../../graphql/rates/mutation';
import {
  SnackVariableInterface,
  displaySnackBar,
} from '../../../common/displaySnackbar';
import ConfirmationButtons from '../../ConfirmationButtons/ConfirmationButtons';

interface OtherRates {
  firstInsurance: string;
  secondInsurance: string;
  thirdInsurance: string;
  firstInstallment: string;
  secondInstallment: string;
  thirdInstallment: string;
  fourthInstallment: string;
  vat: string;
  specificOldTax: string;
  otherOldTax: string;
  newTax: string;
  realEstateSecurity: string;
  honoraires: string;
}

interface EditOtherTaxesProps {
  otherRates: {
    firstInsurance: number;
    secondInsurance: number;
    thirdInsurance: number;
    firstInstallment: number;
    secondInstallment: number;
    thirdInstallment: number;
    fourthInstallment: number;
    vat: number;
    specificOldTax: number;
    otherOldTax: number;
    newTax: number;
    realEstateSecurity: number;
    honoraires: number;
  };
  handleClose: () => void;
}

const EditOtherTaxes = (props: EditOtherTaxesProps) => {
  const { otherRates, handleClose } = props;

  const snackBarData: SnackVariableInterface = {
    type: 'ERROR',
    message: '',
    isOpen: true,
  };
  const client = useApolloClient();

  const [updateTaxes] = useMutation<FloatingRates, EditFloatingRateVariables>(
    EDIT_FLOATING_RATE,
    {
      onCompleted: () => {
        displaySnackBar(client, {
          type: 'SUCCESS',
          isOpen: true,
          message: 'Modification réussie',
        });
      },
      onError: () => {
        displaySnackBar(client, {
          type: 'ERROR',
          isOpen: true,
          message: 'Une érreur est survenue lors de la modification',
        });
      },
    },
  );

  const [newOtherRates, setNewOtherRates] = useState<OtherRates>({
    firstInsurance: (otherRates.firstInsurance * 100).toString(),
    secondInsurance: (otherRates.secondInsurance * 100).toString(),
    thirdInsurance: (otherRates.thirdInsurance * 100).toString(),
    firstInstallment: (otherRates.firstInstallment * 100).toString(),
    secondInstallment: (otherRates.secondInstallment * 100).toString(),
    thirdInstallment: (otherRates.thirdInstallment * 100).toString(),
    fourthInstallment: (otherRates.fourthInstallment * 100).toString(),
    vat: (otherRates.vat * 100).toString(),
    specificOldTax: (otherRates.specificOldTax * 100).toString(),
    otherOldTax: (otherRates.otherOldTax * 100).toString(),
    newTax: (otherRates.newTax * 100).toString(),
    realEstateSecurity: (otherRates.realEstateSecurity * 100).toString(),
    honoraires: otherRates.honoraires.toString(),
  });

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setNewOtherRates({
      ...newOtherRates,
      [event.target.name]: event.target.value,
    });
  };

  const handleEditTaxes = () => {
    updateTaxes({
      variables: {
        firstInsurance: Number(newOtherRates.firstInsurance) / 100,
        secondInsurance: Number(newOtherRates.secondInsurance) / 100,
        thirdInsurance: Number(newOtherRates.thirdInsurance) / 100,
        firstInstallment: Number(newOtherRates.firstInstallment) / 100,
        secondInstallment: Number(newOtherRates.secondInstallment) / 100,
        thirdInstallment: Number(newOtherRates.thirdInstallment) / 100,
        fourthInstallment: Number(newOtherRates.fourthInstallment) / 100,
        vat: Number(newOtherRates.vat) / 100,
        specificOldTax: Number(newOtherRates.specificOldTax) / 100,
        otherOldTax: Number(newOtherRates.otherOldTax) / 100,
        newTax: Number(newOtherRates.newTax) / 100,
        realEstateSecurity: Number(newOtherRates.realEstateSecurity) / 100,
        honoraires: Number(newOtherRates.honoraires),
      },
    });

    handleClose();
  };

  const classes = useStyles();

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      aria-labelledby="form-dialog-title">
      <DialogTitle className={classes.dialog} id="form-dialog-title">
        Autres taxes
      </DialogTitle>

      <DialogContent className="grey-scroll-bar">
        {Object.keys(otherRatesInfo).map((key) => {
          return (
            <>
              <Typography {...{ key, className: classes.groupLabel }}>
                {key}
              </Typography>

              {(otherRatesInfo as any)[key].map(
                (rateInfo: any, index: number) => (
                  <Box key={index}>
                    <TextField
                      autoFocus
                      fullWidth
                      {...{
                        id: 'teg',
                        margin: 'dense',
                        type: 'number',
                        label: rateInfo.label,
                        name: rateInfo.key,
                        value: (newOtherRates as any)[rateInfo.key],
                        onChange: handleChange,
                        inputProps: { min: 0, step: 0.1 },
                      }}
                    />
                  </Box>
                ),
              )}
            </>
          );
        })}
      </DialogContent>

      <ConfirmationButtons
        handleCancel={handleClose}
        handleConfirm={handleEditTaxes}
      />
    </Dialog>
  );
};

export default EditOtherTaxes;
